import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  RotateCcw,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import SafeImage from "./SafeImage";
import { imageUrl } from "../utils/endpoints";

const MIN_ZOOM = -0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getImageName = (image) =>
  image?.originalName || image?.filename || image?.name || "Image";

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return "Size unavailable";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const formatUploadDate = (value) => {
  if (!value) return "Upload date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Upload date unavailable";
  return `Added ${date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const isEditableTarget = (target) => {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true']")
  );
};

const ActionButton = ({ label, children, className = "", ...props }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-35 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const ImagePreviewModal = ({
  images = [],
  activeIndex = 0,
  isOpen,
  onNavigate,
  onClose,
  onRequestDelete,
}) => {
  const canvasRef = useRef(null);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [mode, setMode] = useState("fit");
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const currentImage = images[activeIndex] || null;
  const imageKey =
    currentImage?._id || currentImage?.id || currentImage?.url || activeIndex;
  const resolvedUrl = imageUrl(currentImage?.url);
  const imageName = getImageName(currentImage);

  const fitScale = useMemo(() => {
    if (
      !naturalSize.width ||
      !naturalSize.height ||
      !canvasSize.width ||
      !canvasSize.height
    ) {
      return 1;
    }
    return Math.min(
      canvasSize.width / naturalSize.width,
      canvasSize.height / naturalSize.height,
      1
    );
  }, [canvasSize, naturalSize]);

  const effectiveScale = mode === "fit" ? fitScale : scale;

  const clampPan = useCallback(
    (nextPan, nextScale = effectiveScale) => {
      if (!naturalSize.width || !naturalSize.height) return { x: 0, y: 0 };
      const maxX = Math.max(
        0,
        (naturalSize.width * nextScale - canvasSize.width) / 2
      );
      const maxY = Math.max(
        0,
        (naturalSize.height * nextScale - canvasSize.height) / 2
      );
      return {
        x: clamp(nextPan.x, -maxX, maxX),
        y: clamp(nextPan.y, -maxY, maxY),
      };
    },
    [canvasSize, effectiveScale, naturalSize]
  );

  const resetToFit = useCallback(() => {
    setMode("fit");
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomTo = useCallback(
    (nextScale, anchor) => {
      if (!naturalSize.width || !naturalSize.height) return;

      const clampedScale = clamp(nextScale, MIN_ZOOM, MAX_ZOOM);
      let nextPan = pan;

      if (anchor && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const pointX = anchor.clientX - (rect.left + rect.width / 2);
        const pointY = anchor.clientY - (rect.top + rect.height / 2);
        const imageX = (pointX - pan.x) / effectiveScale;
        const imageY = (pointY - pan.y) / effectiveScale;
        nextPan = {
          x: pointX - imageX * clampedScale,
          y: pointY - imageY * clampedScale,
        };
      }

      setMode("manual");
      setScale(clampedScale);
      setPan(clampPan(nextPan, clampedScale));
    },
    [clampPan, effectiveScale, naturalSize, pan]
  );

  const zoomIn = useCallback(
    () => zoomTo(Math.max(MIN_ZOOM, effectiveScale + ZOOM_STEP)),
    [effectiveScale, zoomTo]
  );

  const zoomOut = useCallback(() => {
    if (effectiveScale <= MIN_ZOOM) return;
    zoomTo(effectiveScale - ZOOM_STEP);
  }, [effectiveScale, zoomTo]);

  const navigate = useCallback(
    (direction) => {
      if (images.length <= 1) return;
      onNavigate?.(activeIndex + direction);
    },
    [activeIndex, images.length, onNavigate]
  );

  useEffect(() => {
    resetToFit();
    setNaturalSize({ width: 0, height: 0 });
  }, [imageKey, resetToFit]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updateCanvasSize = () => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) setCanvasSize({ width: rect.width, height: rect.height });
    };

    updateCanvasSize();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateCanvasSize)
        : null;
    if (canvasRef.current && observer) observer.observe(canvasRef.current);
    window.addEventListener("resize", updateCanvasSize);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (mode === "fit") {
      setPan({ x: 0, y: 0 });
      return;
    }
    setPan((current) => clampPan(current, scale));
  }, [canvasSize, clampPan, mode, scale]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        navigate(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        navigate(1);
      } else if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomIn();
      } else if (event.key === "-") {
        event.preventDefault();
        zoomOut();
      } else if (event.key === "0") {
        event.preventDefault();
        resetToFit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, navigate, onClose, resetToFit, zoomIn, zoomOut]);

  const handleWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Once the image is larger than the canvas, a normal wheel/trackpad
    // gesture scrolls across it for easier reading. Ctrl/Cmd + wheel keeps
    // cursor-anchored zoom available at every scale.
    if (mode === "manual" && canPan && !event.ctrlKey && !event.metaKey) {
      const horizontalDelta = event.shiftKey
        ? event.deltaY + event.deltaX
        : event.deltaX;
      const verticalDelta = event.shiftKey ? 0 : event.deltaY;
      setPan((currentPan) =>
        clampPan(
          {
            x: currentPan.x - horizontalDelta,
            y: currentPan.y - verticalDelta,
          },
          effectiveScale
        )
      );
      return;
    }

    if (event.deltaY > 0 && effectiveScale <= MIN_ZOOM) return;
    const factor = Math.exp(-event.deltaY * 0.0015);
    zoomTo(Math.max(MIN_ZOOM, effectiveScale * factor), event);
  };

  const handleImageClick = (event) => {
    event.stopPropagation();
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }

    if (mode === "fit") {
      zoomTo(1, event);
    } else if (Math.abs(scale - 1) < 0.001) {
      resetToFit();
    } else {
      zoomTo(1, event);
    }
  };

  const handleBackdropClick = (event) => {
    // Only the empty overlay/canvas closes the viewer. Image gestures and
    // controls stop propagation or have their own targets.
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const canPan =
    naturalSize.width * effectiveScale > canvasSize.width + 1 ||
    naturalSize.height * effectiveScale > canvasSize.height + 1;

  const handlePointerDown = (event) => {
    if (event.button !== 0 || !canPan) return;
    event.preventDefault();
    movedRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
    setPan(
      clampPan(
        { x: drag.panX + dx, y: drag.panY + dy },
        effectiveScale
      )
    );
  };

  const finishDragging = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  if (!isOpen || !currentImage || typeof document === "undefined") return null;

  const zoomLabel = mode === "fit" ? "Fit" : `${Math.round(scale * 100)}%`;
  const hasMultipleImages = images.length > 1;
  const imageStyle = {
    transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${effectiveScale})`,
    transformOrigin: "center center",
    touchAction: "none",
  };

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Image preview: ${imageName}`}
      tabIndex={-1}
      className="fixed inset-0 z-[80] overflow-hidden bg-black/90 text-white outline-none backdrop-blur-sm"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={handleBackdropClick}
    >
      <div className="absolute right-3 top-3 z-30 flex max-w-[calc(100vw-1.5rem)] items-center gap-1.5 sm:right-5 sm:top-5 sm:gap-2">
        <span className="hidden min-w-[3.25rem] rounded-full bg-black/55 px-3 py-2 text-center text-xs font-semibold text-white/90 sm:inline-block">
          {zoomLabel}
        </span>
        <a
          href={resolvedUrl}
          download={imageName}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download"
          title="Download"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          onClick={(event) => event.stopPropagation()}
        >
          <Download className="h-5 w-5" />
        </a>
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in new tab"
          title="Open in new tab"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLink className="h-5 w-5" />
        </a>
        <ActionButton
          label="Zoom out"
          onClick={(event) => {
            event.stopPropagation();
            zoomOut();
          }}
          disabled={effectiveScale <= MIN_ZOOM}
        >
          <ZoomOut className="h-5 w-5" />
        </ActionButton>
        <ActionButton
          label="Zoom in"
          onClick={(event) => {
            event.stopPropagation();
            zoomIn();
          }}
          disabled={mode === "manual" && scale >= MAX_ZOOM}
        >
          <ZoomIn className="h-5 w-5" />
        </ActionButton>
        <ActionButton
          label="Reset to fit"
          onClick={(event) => {
            event.stopPropagation();
            resetToFit();
          }}
          disabled={mode === "fit" && pan.x === 0 && pan.y === 0}
        >
          <RotateCcw className="h-5 w-5" />
        </ActionButton>
        <ActionButton
          label="Close"
          className="bg-white/15 hover:bg-white/25"
          onClick={(event) => {
            event.stopPropagation();
            onClose?.();
          }}
        >
          <X className="h-6 w-6" />
        </ActionButton>
      </div>

      <div
        ref={canvasRef}
        className="absolute inset-x-0 bottom-[8.75rem] top-[4.75rem] flex select-none items-center justify-center overflow-hidden sm:bottom-[7.75rem] sm:top-[4.5rem]"
        onWheel={handleWheel}
        onClick={handleBackdropClick}
      >
        <div
          className={`flex flex-none items-center justify-center will-change-transform ${isDragging
              ? "cursor-grabbing"
              : canPan
                ? "cursor-grab"
                : mode === "fit"
                  ? "cursor-zoom-in"
                  : "cursor-zoom-out"
            } ${isDragging ? "" : "transition-transform duration-200 ease-out"}`}
          style={imageStyle}
          onClick={handleImageClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishDragging}
          onPointerCancel={finishDragging}
        >
          <SafeImage
            key={imageKey}
            src={currentImage.url}
            alt={imageName}
            fallback="note"
            draggable={false}
            className="block max-w-none rounded-sm object-contain shadow-2xl"
            onLoad={(event) => {
              setNaturalSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              });
              resetToFit();
            }}
          />
        </div>
      </div>

      {hasMultipleImages && (
        <>
          <ActionButton
            label="Previous image"
            className="absolute left-3 top-1/2 z-30 -translate-y-1/2 sm:left-5"
            onClick={(event) => {
              event.stopPropagation();
              navigate(-1);
            }}
          >
            <ChevronLeft className="h-7 w-7" />
          </ActionButton>
          <ActionButton
            label="Next image"
            className="absolute right-3 top-1/2 z-30 -translate-y-1/2 sm:right-5"
            onClick={(event) => {
              event.stopPropagation();
              navigate(1);
            }}
          >
            <ChevronRight className="h-7 w-7" />
          </ActionButton>
        </>
      )}

      <div className="absolute inset-x-0 bottom-0 z-30 border-t border-white/15 bg-black/75 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md sm:px-6 sm:py-4">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-center sm:text-left">
            <h2 className="truncate text-base font-semibold sm:text-lg" title={imageName}>
              {imageName}
            </h2>
            <p className="mt-0.5 truncate text-xs text-white/70 sm:text-sm">
              {formatUploadDate(currentImage.uploadedAt)}
              <span aria-hidden="true"> • </span>
              {formatFileSize(currentImage.size)}
              {hasMultipleImages && (
                <>
                  <span aria-hidden="true"> • </span>
                  {activeIndex + 1} of {images.length}
                </>
              )}
            </p>
            {mode === "manual" && canPan && (
              <p className="mt-1 hidden text-xs text-white/50 md:block">
                Scroll to move around • Ctrl/Cmd + scroll to zoom
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-center gap-1 sm:gap-2">
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-white transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:px-3"
              onClick={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Open in new tab</span>
              <span className="sm:hidden">Open</span>
            </a>
            <a
              href={resolvedUrl}
              download={imageName}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-white transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:px-3"
              onClick={(event) => event.stopPropagation()}
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </a>
            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-red-200 transition-colors hover:bg-red-500/20 hover:text-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 sm:px-3"
              onClick={(event) => {
                event.stopPropagation();
                onRequestDelete?.(currentImage);
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImagePreviewModal;
